import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { formatNum, formatSgd, apiErr } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui/table";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip as RTooltip,
  CartesianGrid, Legend, BarChart, Cell, ScatterChart, Scatter, ZAxis,
} from "recharts";
import {
  Gauge, TrendingUp, Sparkles, Trophy, AlertTriangle,
  Building2, Lightbulb, ShieldCheck, Target, ClipboardList, Activity, Database,
} from "lucide-react";
import { toast } from "sonner";

const RAG = {
  green: "text-[#00E65B]", amber: "text-[#FFB800]", red: "text-red-500", grey: "text-muted-foreground",
};
const RAG_BG = {
  green: "bg-[#00E65B]", amber: "bg-[#FFB800]", red: "bg-red-500", grey: "bg-muted-foreground/40",
};
const RAG_LABEL = { green: "On track", amber: "Watchlist", red: "Off track", grey: "—" };

function ScoreGauge({ score, prev, rag, year }) {
  const hasPrev = prev !== null && prev !== undefined;
  const delta = hasPrev ? score - prev : null;
  return (
    <div className="card-flat p-5 group" data-testid="opeff-score">
      <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> Overall Operational Efficiency <ProvenanceTrigger pkey="op.score" year={year} className="ml-auto" /></div>
      <div className="flex items-end gap-3">
        <div className={`font-mono text-5xl font-bold tracking-tighter ${RAG[rag]}`}>{score}<span className="text-lg text-muted-foreground">/100</span></div>
        {hasPrev ? (
          <div className={`mb-2 text-sm font-medium ${delta >= 0 ? "text-[#00E65B]" : "text-red-500"}`}>
            {delta >= 0 ? "+" : ""}{delta} vs PY
          </div>
        ) : (
          <div className="mb-2 text-xs text-muted-foreground" data-testid="opeff-first-year">First reporting year</div>
        )}
      </div>
      <Progress value={score} className="h-2 mt-3" />
      <div className="text-xs text-muted-foreground mt-1.5">{RAG_LABEL[rag]}{hasPrev ? ` · prior ${prev}` : " · no prior-year history"}</div>
    </div>
  );
}

function OverviewTab({ d }) {
  const ex = d.executive;
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/operational/ai/commentary?year=${d.year}`);
      setSummary(r.data.commentary);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const interp = { positive: "text-[#00E65B]", negative: "text-red-500", neutral: "text-muted-foreground" }[ex.productivity_interpretation];
  return (
    <div className="space-y-5 mt-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ScoreGauge score={ex.efficiency_score} prev={ex.efficiency_score_prev} rag={ex.rag} year={d.year} />
        <div className="card-flat p-5 group" data-testid="opeff-productivity">
          <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> ESG Productivity Index <ProvenanceTrigger pkey="op.productivity" year={d.year} className="ml-auto" /></div>
          <div className={`font-mono text-5xl font-bold tracking-tighter ${interp}`}>{ex.first_reporting_year ? "—" : (ex.productivity_index > 0 ? "+" : "") + ex.productivity_index}</div>
          <div className="text-xs text-muted-foreground mt-2">
            {ex.first_reporting_year
              ? "Insufficient prior-year data — the index needs a prior reporting year to compute."
              : <>Revenue <span className="text-foreground font-medium">{ex.revenue_growth_pct > 0 ? "+" : ""}{ex.revenue_growth_pct}%</span> vs Emissions <span className="text-foreground font-medium">{ex.emissions_growth_pct > 0 ? "+" : ""}{ex.emissions_growth_pct}%</span></>}
          </div>
          {!ex.first_reporting_year && <div className={`text-xs mt-1 capitalize font-medium ${interp}`}>{ex.productivity_interpretation} — output {ex.productivity_interpretation === "positive" ? "outpacing" : "vs"} ESG impact</div>}
        </div>
        <div className="card-flat p-5 group" data-testid="opeff-dq-headline">
          <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Efficiency Data Quality <ProvenanceTrigger pkey="op.data_quality" year={d.year} className="ml-auto" /></div>
          <div className="font-mono text-5xl font-bold tracking-tighter">{ex.data_quality_score}<span className="text-lg text-muted-foreground">/100</span></div>
          <Progress value={ex.data_quality_score} className="h-2 mt-3" />
          <div className="text-xs text-muted-foreground mt-1.5">Efficiency inputs only — 50% driver completeness + 50% verified records</div>
        </div>
      </div>

      <div className="card-flat p-4">
        <div className="label-eyebrow mb-3">Efficiency status by material issue</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {ex.material_status.map((m) => (
            <div key={m.issue} className="border border-border rounded-sm p-3" data-testid={`opeff-issue-${m.issue.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`h-2.5 w-2.5 rounded-full ${RAG_BG[m.status]}`} />
                <span className={`text-[10px] uppercase font-medium ${RAG[m.status]}`}>{RAG_LABEL[m.status]}</span>
              </div>
              <div className="text-sm font-medium leading-tight">{m.issue}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{m.owner}</div>
              <Progress value={m.progress} className="h-1 mt-2" />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="card-flat p-4">
          <div className="label-eyebrow mb-3">Top 5 efficiency variances</div>
          <div className="space-y-2">
            {ex.top_variances.map((v, i) => (
              <div key={i} className="flex items-start gap-3 border-b border-border pb-2 last:border-0">
                <span className={`font-mono text-sm font-bold w-16 shrink-0 ${v.direction === "favourable" ? "text-[#00E65B]" : "text-red-500"}`}>{v.variance_pct > 0 ? "+" : ""}{v.variance_pct}%</span>
                <div className="min-w-0">
                  <div className="text-sm font-medium leading-tight">{v.metric}</div>
                  <div className="text-[11px] text-muted-foreground">{v.likely_cause} · {v.owner}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="card-flat p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI executive summary</div>
            <Button size="sm" variant="primary" onClick={gen} disabled={busy} data-testid="opeff-generate-summary">
              {busy ? "Generating…" : summary ? "Regenerate" : "Generate"}
            </Button>
          </div>
          {summary
            ? <p className="text-sm leading-relaxed" data-testid="opeff-summary-text">{summary}</p>
            : <p className="text-sm text-muted-foreground">Generate an AI summary explaining what changed in operational efficiency and why, plus the priority action.</p>}
        </div>
      </div>
    </div>
  );
}

function DriverTab({ d }) {
  const drv = d.drivers;
  const wf = drv.decomposition.filter((x) => x.value !== 0 || x.driver === "Boundary Change");
  const hasActivity = (drv.esg_vs_activity || []).some((x) => x.activity !== null && x.activity !== undefined);
  return (
    <div className="space-y-4 mt-4">
      <div className="card-flat p-4">
        <div className="label-eyebrow mb-1 flex items-center gap-1.5">ESG vs business activity{hasActivity ? ` (${drv.activity_unit})` : ""} <ProvenanceTrigger pkey="op.drivers" year={d.year} className="!opacity-60 hover:!opacity-100" /></div>
        <p className="text-xs text-muted-foreground mb-3">
          {hasActivity
            ? "Monthly total emissions (bars, tCO₂e) against business activity (line). Divergence signals efficiency change."
            : "Monthly total emissions (bars, tCO₂e). No business-activity series is configured yet — add operational drivers to overlay activity."}
        </p>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={drv.esg_vs_activity} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="period" tick={{ fontSize: 10 }} tickFormatter={(p) => p?.slice(5)} />
            <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
            {hasActivity && <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />}
            <RTooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="l" dataKey="emissions" name="Emissions (tCO₂e)" fill="#00E65B" opacity={0.55} radius={[2, 2, 0, 0]} />
            {hasActivity && <Line yAxisId="r" type="monotone" dataKey="activity" name="Activity" stroke="#FFB800" strokeWidth={2} dot={false} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="card-flat p-4">
          <div className="label-eyebrow mb-1">Driver decomposition</div>
          {drv.insufficient_history ? (
            <div className="text-sm text-muted-foreground py-12 text-center px-4" data-testid="decomp-insufficient-history">
              Insufficient history — driver decomposition needs at least one prior reporting year.
              FY{d.year} is the first year with committed data, so no year-on-year split is shown.
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-3">YoY change in total emissions: {drv.total_change > 0 ? "+" : ""}{formatNum(drv.total_change, { decimals: 1 })} tCO₂e, split by driver.</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={wf} layout="vertical" margin={{ left: 30, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="driver" tick={{ fontSize: 10 }} width={130} />
                  <RTooltip />
                  <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                    {wf.map((x, i) => <Cell key={i} fill={x.value > 0 ? "#FF5555" : "#00E65B"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow mb-3">Denominator integrity</div>
          {(drv.denominator_integrity || []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center px-4" data-testid="denominator-empty">
              No denominator sources configured yet — add operational drivers (revenue, headcount, floor area) to track completeness.
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {drv.denominator_integrity.map((x, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span>{x.driver}</span>
                      <span className={`font-mono ${x.completeness_pct >= 90 ? "text-[#00E65B]" : x.completeness_pct >= 50 ? "text-[#FFB800]" : "text-red-500"}`}>{x.completeness_pct}%</span>
                    </div>
                    <Progress value={x.completeness_pct} className="h-1" />
                  </div>
                ))}
              </div>
              {drv.denominator_integrity[0]?.source && (
                <div className="text-[10px] text-muted-foreground mt-3" data-testid="denominator-source">
                  Source: {drv.denominator_integrity[0].source}{drv.denominator_integrity[0].last_updated ? ` · updated ${drv.denominator_integrity[0].last_updated}` : ""}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BenchmarkTab({ d, onSynced }) {
  const b = d.benchmarking;
  const [syncing, setSyncing] = useState(false);
  const [byGroup, setByGroup] = useState(false);
  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/operational/facilities/sync");
      toast.success(r.data.created ? `Synced — ${r.data.created} twin site(s) created` : "Twin already in sync with the facility master");
      onSynced && onSynced();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    setSyncing(false);
  };
  const scatter = b.facilities.filter((f) => f.status !== "grey").map((f) => ({ x: Number(f.floor_area_sqm) || 0, y: Number(f.energy_intensity_kwh_sqm) || 0, z: Number(f.emissions_tco2e) || 0, name: f.name, status: f.status }));
  const gslug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const groupedRanking = Object.entries(b.facilities.reduce((m, f) => { (m[f.group || "Ungrouped"] ||= []).push(f); return m; }, {}))
    .map(([group, facs]) => {
      const ranked = facs.filter((x) => !x.incomplete).sort((a, c) => (a.energy_intensity_kwh_sqm ?? 1e9) - (c.energy_intensity_kwh_sqm ?? 1e9));
      const rankMap = new Map(ranked.map((x, i) => [x.name, i + 1]));
      return { group, facs: facs.map((x) => ({ ...x, grank: rankMap.get(x.name) })) };
    }).sort((a, c) => a.group.localeCompare(c.group));
  const yMax = Math.max(1, Math.ceil(Math.max(0, ...scatter.map((s) => s.y)) * 1.2));
  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {b.best && (
          <div className="card-flat p-4 border-l-2 border-l-[#00E65B]" data-testid="opeff-best">
            <div className="label-eyebrow text-[10px] inline-flex items-center gap-1 text-[#00E65B]"><Trophy className="h-3.5 w-3.5" /> Best performer</div>
            <div className="font-display font-semibold mt-1">{b.best.name}</div>
            <div className="font-mono text-2xl font-bold mt-1">{b.best.energy_intensity_kwh_sqm} <span className="text-xs text-muted-foreground">kWh/sqm</span></div>
            <div className="text-xs text-muted-foreground mt-1">{b.best.region} · {b.best.owner} · {b.best.yoy_pct > 0 ? "+" : ""}{b.best.yoy_pct}% YoY</div>
          </div>
        )}
        {b.worst && (
          <div className="card-flat p-4 border-l-2 border-l-red-500" data-testid="opeff-worst">
            <div className="label-eyebrow text-[10px] inline-flex items-center gap-1 text-red-500"><AlertTriangle className="h-3.5 w-3.5" /> Needs attention</div>
            <div className="font-display font-semibold mt-1">{b.worst.name}</div>
            <div className="font-mono text-2xl font-bold mt-1">{b.worst.energy_intensity_kwh_sqm} <span className="text-xs text-muted-foreground">kWh/sqm</span></div>
            <div className="text-xs text-muted-foreground mt-1">{b.worst.region} · {b.worst.owner} · {b.worst.yoy_pct > 0 ? "+" : ""}{b.worst.yoy_pct}% YoY</div>
          </div>
        )}
      </div>

      <div className="card-flat overflow-hidden">
        <div className="flex items-center justify-between p-3 pb-0">
          <div className="label-eyebrow">Facility efficiency ranking (energy intensity){byGroup ? " · within group" : ""}</div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-sm border border-border overflow-hidden" data-testid="opeff-benchmark-view">
              {[["overall", "Overall"], ["group", "Within group"]].map(([v, l]) => (
                <button key={v} onClick={() => setByGroup(v === "group")} data-testid={`opeff-benchmark-${v}`}
                  className={`px-2.5 py-1 text-[11px] font-mono transition-colors ${(byGroup ? "group" : "overall") === v
                    ? "bg-accent text-accent-foreground font-bold"
                    : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"}`}>{l}</button>
              ))}
            </div>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={sync} disabled={syncing} data-testid="opeff-sync-btn">
              {syncing ? "Syncing…" : "Sync from facility master"}
            </Button>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Rank</TableHead><TableHead>Facility</TableHead>
              {!byGroup && <TableHead>Group</TableHead>}
              <TableHead className="text-right">kWh/sqm</TableHead><TableHead className="text-right">YoY</TableHead>
              <TableHead className="text-right">Floor (sqm)</TableHead><TableHead>Status</TableHead><TableHead>Owner</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!byGroup && b.facilities.map((f) => (
              <TableRow key={f.name} data-testid={`opeff-facility-${f.rank}`} className={f.incomplete ? "opacity-60" : ""}>
                <TableCell className="font-mono">{f.incomplete ? "—" : f.rank}</TableCell>
                <TableCell className="font-medium text-sm">{f.name}<span className="text-muted-foreground text-xs ml-2">{f.region}</span></TableCell>
                <TableCell className="text-xs text-muted-foreground" data-testid={`opeff-facility-group-${f.rank}`}>{f.group || "Ungrouped"}</TableCell>
                <TableCell className="text-right font-mono text-sm">{f.energy_intensity_kwh_sqm ?? "—"}</TableCell>
                <TableCell className={`text-right font-mono text-sm ${(f.yoy_pct || 0) < 0 ? "text-[#00E65B]" : "text-red-500"}`}>{f.yoy_pct == null ? "—" : (f.yoy_pct > 0 ? "+" : "") + f.yoy_pct + "%"}</TableCell>
                <TableCell className="text-right font-mono text-xs">{f.floor_area_sqm ? formatNum(f.floor_area_sqm, { decimals: 0 }) : "—"}</TableCell>
                <TableCell>{f.incomplete
                  ? <Badge variant="warning" className="text-[9px]" title={f.incomplete_reason} data-testid={`opeff-incomplete-${f.rank}`}>incomplete</Badge>
                  : <Badge variant="outline" className={`text-[9px] ${RAG[f.status]}`}>{RAG_LABEL[f.status]}</Badge>}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{f.owner}</TableCell>
              </TableRow>
            ))}
            {byGroup && groupedRanking.map((g) => (
              <React.Fragment key={g.group}>
                <TableRow className="bg-surface/60" data-testid={`opeff-group-header-${gslug(g.group)}`}>
                  <TableCell colSpan={7} className="py-1.5 text-xs font-display font-semibold">{g.group} <span className="text-muted-foreground font-normal">· {g.facs.length} site{g.facs.length > 1 ? "s" : ""}</span></TableCell>
                </TableRow>
                {g.facs.sort((a, c) => (a.grank ?? 1e9) - (c.grank ?? 1e9)).map((f) => (
                  <TableRow key={f.name} data-testid={`opeff-groupfacility-${gslug(g.group)}-${f.grank ?? "x"}`} className={f.incomplete ? "opacity-60" : ""}>
                    <TableCell className="font-mono">{f.incomplete ? "—" : f.grank}</TableCell>
                    <TableCell className="font-medium text-sm" style={{ paddingLeft: 24 }}>{f.name}<span className="text-muted-foreground text-xs ml-2">{f.region}</span></TableCell>
                    <TableCell className="text-right font-mono text-sm">{f.energy_intensity_kwh_sqm ?? "—"}</TableCell>
                    <TableCell className={`text-right font-mono text-sm ${(f.yoy_pct || 0) < 0 ? "text-[#00E65B]" : "text-red-500"}`}>{f.yoy_pct == null ? "—" : (f.yoy_pct > 0 ? "+" : "") + f.yoy_pct + "%"}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{f.floor_area_sqm ? formatNum(f.floor_area_sqm, { decimals: 0 }) : "—"}</TableCell>
                    <TableCell>{f.incomplete
                      ? <Badge variant="warning" className="text-[9px]" title={f.incomplete_reason}>incomplete</Badge>
                      : <Badge variant="outline" className={`text-[9px] ${RAG[f.status]}`}>{RAG_LABEL[f.status]}</Badge>}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{f.owner}</TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="card-flat p-4">
        <div className="label-eyebrow mb-1">Internal benchmark — energy intensity vs floor area</div>
        <p className="text-xs text-muted-foreground mb-3">Bubble size = total emissions. Lower-left is most efficient.</p>
        <ResponsiveContainer width="100%" height={280}>
          <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis type="number" dataKey="x" name="Floor area" unit=" sqm" tick={{ fontSize: 10 }} domain={[0, "auto"]} />
            <YAxis type="number" dataKey="y" name="Energy intensity" unit=" kWh/sqm" tick={{ fontSize: 10 }} domain={[0, yMax]} allowDecimals={false} tickCount={5} />
            <ZAxis type="number" dataKey="z" range={[80, 600]} />
            <RTooltip cursor={{ strokeDasharray: "3 3" }} formatter={(v, n) => [v, n]} labelFormatter={() => ""} />
            <Scatter data={scatter} fill="#00E65B">
              {scatter.map((s, i) => <Cell key={i} fill={s.status === "red" ? "#FF5555" : s.status === "amber" ? "#FFB800" : "#00E65B"} />)}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function OpportunitiesTab({ d, reload }) {
  const o = d.opportunities;
  const [busyId, setBusyId] = useState(null);
  const convert = async (opp) => {
    setBusyId(opp.id);
    try {
      await api.post(`/operational/opportunities/${opp.id}/convert`, { owner: opp.owner });
      toast.success(`Initiative created: ${opp.title}`);
      reload();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusyId(null); }
  };
  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card-flat p-4"><div className="label-eyebrow text-[10px]">Projected energy saving</div><div className="font-mono text-xl font-bold">{formatNum(o.estimated_savings.energy_mwh, { decimals: 0 })} <span className="text-xs text-muted-foreground">MWh</span></div></div>
        <div className="card-flat p-4"><div className="label-eyebrow text-[10px]">Potential emissions avoided</div><div className="font-mono text-xl font-bold">{formatNum(o.estimated_savings.emissions_t, { decimals: 0 })} <span className="text-xs text-muted-foreground">tCO₂e</span></div></div>
        <div className="card-flat p-4"><div className="label-eyebrow text-[10px]">Projected water saving</div><div className="font-mono text-xl font-bold">{formatNum(o.estimated_savings.water_m3, { decimals: 0 })} <span className="text-xs text-muted-foreground">m³</span></div></div>
        <div className="card-flat p-4"><div className="label-eyebrow text-[10px]">Capex (indicative)</div><div className="font-mono text-xl font-bold">{formatSgd(o.estimated_savings.cost_sgd)}</div></div>
      </div>
      <p className="text-[10px] text-muted-foreground -mt-2" data-testid="opeff-savings-caption">
        Projected estimates across the {o.estimated_savings.open_count ?? 0} open pipeline opportunit{(o.estimated_savings.open_count ?? 0) === 1 ? "y" : "ies"} below — nothing here has been realised yet; savings count as realised only once an opportunity is marked Completed.
      </p>
      {o.realized_savings?.count > 0 && (
        <div className="card-flat p-3 text-xs" data-testid="opeff-realized">
          <span className="text-[#00E65B] font-semibold">Realised to date ({o.realized_savings.count} completed):</span>{" "}
          <span className="font-mono">{formatNum(o.realized_savings.energy_mwh, { decimals: 0 })} MWh · {formatNum(o.realized_savings.emissions_t, { decimals: 0 })} tCO₂e · {formatNum(o.realized_savings.water_m3, { decimals: 0 })} m³</span>
        </div>
      )}

      <div className="card-flat p-3">
        <div className="label-eyebrow mb-2">Opportunity pipeline</div>
        <div className="flex flex-wrap gap-2">
          {o.stage_order.map((s) => (
            <div key={s} className="border border-border rounded-sm px-3 py-2 text-center min-w-[90px]">
              <div className="font-mono text-lg font-bold">{o.pipeline[s]}</div>
              <div className="text-[10px] text-muted-foreground capitalize">{s.replace("_", " ")}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="label-eyebrow inline-flex items-center gap-1"><Lightbulb className="h-3.5 w-3.5 text-accent" /> AI efficiency recommendations</div>
        {o.items.map((opp) => (
          <div key={opp.id} className="card-flat p-4" data-testid={`opeff-opp-${opp.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-sm">{opp.title}</div>
                <div className="text-xs text-muted-foreground mt-1">{opp.rationale}</div>
                <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
                  <Badge variant="outline" className="text-[9px]">{opp.linked_kpi}</Badge>
                  <span className="text-[#00E65B]">{opp.expected_impact}</span>
                  <span className="text-muted-foreground">· {opp.owner}</span>
                  <span className="text-muted-foreground">· confidence {Math.round(opp.confidence * 100)}%</span>
                  <Badge variant="outline" className="text-[9px] capitalize">{opp.stage.replace("_", " ")}</Badge>
                </div>
              </div>
              <Button size="sm" variant="outline" disabled={busyId === opp.id} onClick={() => convert(opp)} data-testid={`opeff-convert-${opp.id}`}>
                <Target className="h-3.5 w-3.5" /> {busyId === opp.id ? "…" : "Create initiative"}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {o.off_track.length > 0 && (
        <div className="card-flat p-4">
          <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> Off-track efficiency actions</div>
          <div className="space-y-1.5">
            {o.off_track.map((a, i) => (
              <div key={i} className="flex items-center gap-3 text-sm border-b border-border pb-1.5 last:border-0">
                <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                <span className="flex-1">{a.kpi} — <span className="text-muted-foreground">{a.issue}</span></span>
                <span className="text-xs text-muted-foreground">{a.action}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DataQualityTab({ d }) {
  const dq = d.data_quality;
  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="card-flat p-4"><div className="label-eyebrow text-[10px]">Efficiency DQ score</div><div className="font-mono text-3xl font-bold">{dq.score}</div><Progress value={dq.score} className="h-1.5 mt-2" /><div className="text-[10px] text-muted-foreground mt-1.5">{dq.score_label || "Efficiency inputs only"}</div></div>
        <div className="card-flat p-4"><div className="label-eyebrow text-[10px]">Driver completeness</div><div className="font-mono text-3xl font-bold">{dq.driver_completeness}%</div></div>
        <div className="card-flat p-4"><div className="label-eyebrow text-[10px]">Data verification</div><div className="font-mono text-3xl font-bold">{dq.datapoint_verification_pct}%</div></div>
        <div className="card-flat p-4"><div className="label-eyebrow text-[10px]">Evidence coverage</div><div className="font-mono text-3xl font-bold">{dq.evidence_coverage_pct}%</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="card-flat p-4">
          <div className="label-eyebrow mb-2">Missing / incomplete operational drivers</div>
          {dq.missing_drivers.length ? dq.missing_drivers.map((m, i) => (
            <div key={i} className="flex items-center justify-between text-sm border-b border-border py-1.5 last:border-0">
              <span className="capitalize">{m.driver.replace("_", " ")}</span>
              <span className="flex items-center gap-2">
                <Badge variant="outline" className={`text-[9px] ${m.severity === "high" ? "text-red-500" : m.severity === "medium" ? "text-[#FFB800]" : "text-muted-foreground"}`}>{m.severity}</Badge>
                <span className="font-mono text-xs">{m.completeness_pct}%</span>
              </span>
            </div>
          )) : <div className="text-sm text-muted-foreground py-4 text-center">All denominators complete.</div>}
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow mb-2">Anomalies affecting efficiency</div>
          {dq.anomalies.length ? dq.anomalies.map((a, i) => (
            <div key={i} className="flex items-start gap-2 text-sm border-b border-border py-1.5 last:border-0">
              <AlertTriangle className="h-4 w-4 text-[#FFB800] mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">{a.metric} <span className="text-muted-foreground text-xs">· {a.facility}</span></div>
                <div className="text-[11px] text-muted-foreground capitalize">{a.type?.replace(/_/g, " ")} · {a.severity}</div>
              </div>
            </div>
          )) : <div className="text-sm text-muted-foreground py-4 text-center">No open anomalies.</div>}
        </div>
      </div>
    </div>
  );
}

export default function OperationalEfficiency() {
  const [years, setYears] = useState([new Date().getFullYear()]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/operational/dashboard?year=${year}`)
      .then((r) => {
        setD(r.data);
        if (r.data.available_years?.length) {
          setYears(r.data.available_years);
          if (!r.data.available_years.includes(year)) setYear(Math.max(...r.data.available_years));
        }
      })
      .catch(() => setD({ empty: true }))
      .finally(() => setLoading(false));
  }, [year]);
  useEffect(() => { load(); }, [load]);

  if (loading && !d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading operational efficiency…</div>;
  if (!d || d.empty) {
    return (
      <div className="p-8"><div className="card-flat p-12 text-center">
        <h2 className="font-display text-2xl font-bold">No operational data yet.</h2>
        <p className="text-muted-foreground mt-2">{d?.note || "Operational efficiency requires ESG data and operational drivers."}</p>
      </div></div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="operational-efficiency-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Control Tower · Operational Efficiency · FY{year}</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3">
            <Activity className="h-8 w-8 text-accent" /> Operational Efficiency
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            How efficiently {d.tenant_name} operates behind the ESG numbers — intensity, productivity, drivers, site benchmarking and improvement opportunities.
          </p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28" data-testid="opeff-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>FY{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="opeff-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="opeff-tab-drivers">Driver Analysis</TabsTrigger>
          <TabsTrigger value="benchmark" data-testid="opeff-tab-benchmark">Site Benchmarking</TabsTrigger>
          <TabsTrigger value="opportunities" data-testid="opeff-tab-opportunities">Opportunities</TabsTrigger>
          <TabsTrigger value="quality" data-testid="opeff-tab-quality">Data Quality</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab d={d} /></TabsContent>
        <TabsContent value="drivers"><DriverTab d={d} /></TabsContent>
        <TabsContent value="benchmark"><BenchmarkTab d={d} onSynced={load} /></TabsContent>
        <TabsContent value="opportunities"><OpportunitiesTab d={d} reload={load} /></TabsContent>
        <TabsContent value="quality"><DataQualityTab d={d} /></TabsContent>
      </Tabs>
    </div>
  );
}
